import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface AuthUser {
  id: string;
  email: string;
  username: string | null;
  display_name: string;
  status: string;
  email_verified: boolean;
}

interface AuthState {
  // Access token lives in memory only — NOT persisted to localStorage.
  // On page reload it is null; ProtectedRoute will re-obtain it via the refresh token.
  accessToken: string | null;

  // Refresh token and user info are persisted so the session survives page
  // reloads — but only when the user chose "Remember me" at login.
  refreshToken: string | null;
  user: AuthUser | null;
  remember: boolean;

  setLogin: (accessToken: string, refreshToken: string, user: AuthUser, remember?: boolean) => void;
  setAccessToken: (token: string) => void;
  updateUser: (patch: Partial<AuthUser>) => void;
  clearAuth: () => void;
  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      remember: true,

      // `remember` is only passed by the login form; token-refresh flows omit
      // it and keep whatever the user chose when they signed in.
      setLogin: (accessToken, refreshToken, user, remember) =>
        set((s) => ({ accessToken, refreshToken, user, remember: remember ?? s.remember })),

      setAccessToken: (accessToken) => set({ accessToken }),

      updateUser: (patch) => set((s) => ({ user: s.user ? { ...s.user, ...patch } : s.user })),

      clearAuth: () => set({ accessToken: null, refreshToken: null, user: null }),

      isAuthenticated: () => get().accessToken !== null,
    }),
    {
      name: "ai-finops-auth",
      partialize: (s) =>
        // accessToken intentionally excluded — memory-only for XSS mitigation.
        // Without "Remember me", the refresh token stays memory-only too, so
        // closing the tab genuinely ends the session.
        s.remember
          ? { refreshToken: s.refreshToken, user: s.user, remember: s.remember }
          : { remember: s.remember },
    },
  ),
);
